import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Πόσο κοστίζει ένα μικρό τατουάζ στην Αθήνα;", acceptedAnswer: { "@type": "Answer", text: "Η τιμή εξαρτάται από το συγκεκριμένο σχέδιο, το μέγεθος, τη λεπτομέρεια, το χρώμα και το σημείο του σώματος. Δεν υπάρχει μία σταθερή τιμή για όλα τα μικρά τατουάζ. Για ακριβή προσφορά, στείλε μήνυμα στο 210 Tattoo Athens στο Instagram και δείξε τους την ιδέα σου." } },
    { "@type": "Question", name: "Υπάρχει ελάχιστη χρέωση για πολύ μικρό τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Ναι, σχεδόν κάθε σοβαρό στούντιο τατουάζ έχει μια ελάχιστη χρέωση. Αυτή καλύπτει το βασικό κόστος προετοιμασίας, τα στείρα υλικά μιας χρήσης και τον χρόνο του καλλιτέχνη, ανεξάρτητα από το πόσο μικρό είναι το σχέδιο." } },
    { "@type": "Question", name: "Γιατί ένα μικρό τατουάζ μπορεί να κοστίζει περισσότερο από όσο περίμενα;", acceptedAnswer: { "@type": "Answer", text: "Επειδή δεν πληρώνεις μόνο το μελάνι. Πληρώνεις την εμπειρία του tattoo artist, τις συνθήκες υγιεινής, τα στείρα υλικά και την ποιότητα που εγγυάται ότι το τατουάζ σου θα αντέξει στον χρόνο." } },
    { "@type": "Question", name: "Πόση ώρα παίρνει ένα μικρό τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Τα περισσότερα μικρά τατουάζ ολοκληρώνονται μέσα στην ίδια μέρα του ραντεβού, σε σχετικά σύντομο χρόνο. Ο ακριβής χρόνος εξαρτάται από τη λεπτομέρεια και το σημείο του σώματος." } },
    { "@type": "Question", name: "Πονάει πολύ ένα μικρό τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Ο πόνος εξαρτάται από το σημείο. Σημεία με περισσότερη σάρκα είναι πιο ανεκτά, ενώ σημεία πάνω από κόκαλο είναι πιο ευαίσθητα. Επειδή τα μικρά τατουάζ τελειώνουν γρήγορα, η όποια ενόχληση δεν κρατάει πολύ." } },
    { "@type": "Question", name: "Είναι το μικρό τατουάζ καλή επιλογή για πρώτη φορά;", acceptedAnswer: { "@type": "Answer", text: "Απόλυτα. Ένα μικρό τατουάζ είναι ιδανικό για όσους κάνουν το πρώτο τους κομμάτι. Σου δίνει την εμπειρία χωρίς μεγάλη δέσμευση και σε βοηθάει να καταλάβεις πώς αντιδρά το δέρμα σου." } },
    { "@type": "Question", name: "Πώς μπορώ να κλείσω ραντεβού στο 210 Tattoo Athens;", acceptedAnswer: { "@type": "Answer", text: "Στείλε μήνυμα στο Instagram του στούντιο, @210tattoo, περίγραψε την ιδέα σου, δείξε τους τυχόν αναφορές που έχεις και η ομάδα θα σε καθοδηγήσει για το σχέδιο, την τιμή και το ραντεβού." } },
  ],
};

export default function SmallTattooCostAthens() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Πόσο κοστίζει ένα μικρό τατουάζ στην Αθήνα;",
      "description": "Όλοι οι παράγοντες που καθορίζουν την τιμή ενός μικρού τατουάζ στην Αθήνα: μέγεθος, λεπτομέρεια, χρώμα, σημείο σώματος και ελάχιστη χρέωση.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-06-29",
      "dateModified": "2026-06-29",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": "https://210tattoo.gr/blog/small-tattoo-cost-athens",
      "inLanguage": "el"
    };
    inject('schema-small-tattoo-cost-faq', faqSchema);
    inject('schema-small-tattoo-cost-article', articleSchema);
    return () => {
      document.getElementById('schema-small-tattoo-cost-faq')?.remove();
      document.getElementById('schema-small-tattoo-cost-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Πόσο κοστίζει ένα μικρό τατουάζ στην Αθήνα; | 210 Tattoo Athens</title>
        <meta name="description" content="Όλοι οι παράγοντες που καθορίζουν την τιμή ενός μικρού τατουάζ στην Αθήνα: μέγεθος, λεπτομέρεια, χρώμα, σημείο σώματος, εμπειρία artist και ελάχιστη χρέωση." />
        <link rel="canonical" href="https://210tattoo.gr/blog/small-tattoo-cost-athens" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog/small-tattoo-cost-athens" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog/small-tattoo-cost-athens" />
        <meta property="og:title" content="Πόσο κοστίζει ένα μικρό τατουάζ στην Αθήνα; | 210 Tattoo Athens" />
        <meta property="og:description" content="Όλοι οι παράγοντες που καθορίζουν την τιμή ενός μικρού τατουάζ στην Αθήνα: μέγεθος, λεπτομέρεια, χρώμα και σημείο σώματος." />
        <meta property="og:url" content="https://210tattoo.gr/blog/small-tattoo-cost-athens" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage('small-tattoo-cost-athens')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Πόσο κοστίζει ένα μικρό τατουάζ στην Αθήνα; | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Τι επηρεάζει την τιμή ενός μικρού τατουάζ στην Αθήνα. Οδηγός κοστολόγησης από το 210 Tattoo Athens." />
        <meta name="twitter:image" content={ogImage('small-tattoo-cost-athens')} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Κόστος Μικρού Τατουάζ Αθήνα</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Πόσο κοστίζει</span>
              <br />
              <span className="text-white">ένα μικρό τατουάζ στην Αθήνα;</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">29 Ιουνίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Αν σκέφτεσαι το πρώτο σου τατουάζ ή ένα ακόμα μικρό κομμάτι, η πρώτη ερώτηση είναι σχεδόν πάντα η ίδια: πόσο κοστίζει; Δες όλους τους παράγοντες πριν κλείσεις ραντεβού.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Intro */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Γιατί δεν υπάρχει <span className="text-pink-500">μία σταθερή τιμή</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Πολλοί περιμένουν έναν τιμοκατάλογο σαν αυτόν που θα έβρισκαν σε ένα μαγαζί με προϊόντα. Το τατουάζ όμως λειτουργεί διαφορετικά. Κάθε σχέδιο είναι μοναδικό, κάθε σώμα είναι διαφορετικό και κάθε καλλιτέχνης έχει τον δικό του τρόπο δουλειάς.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Σκέψου ότι αγοράζεις χρόνο, τέχνη και ασφάλεια. Ο tattoo artist αφιερώνει ώρα στο να σχεδιάσει το κομμάτι, να προετοιμάσει τον χώρο, να στήσει στείρα εργαλεία και να εκτελέσει το τατουάζ με ακρίβεια. Γι' αυτό η τιμή υπολογίζεται για το κάθε σχέδιο ξεχωριστά.
            </p>
          </div>
        </section>

        {/* What counts as small */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι θεωρείται <span className="text-pink-500">μικρό τατουάζ</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Στον κόσμο του tattoo, μικρό θεωρείται συνήθως ένα σχέδιο που χωράει σε μια παλάμη ή και λιγότερο. Στα μικρά τατουάζ μπαίνουν διάφορες κατηγορίες, και η κατηγορία παίζει ρόλο στην τιμή.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Minimal τατουάζ', desc: 'Απλές γραμμές και καθαρό σχέδιο.' },
                { title: 'Fine line τατουάζ', desc: 'Λεπτές, ντελικάτες λεπτομέρειες.' },
                { title: 'Micro τατουάζ', desc: 'Ακόμα πιο μικροσκοπικά, απαιτούν μεγάλη ακρίβεια.' },
                { title: 'Lettering', desc: 'Μικρές λέξεις, ονόματα ή ημερομηνίες.' },
                { title: 'Μικρά σύμβολα', desc: 'Αστεράκια, καρδούλες, βελάκια, γεωμετρικά σχήματα.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Factors */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Οι βασικοί παράγοντες <span className="text-pink-500">που καθορίζουν την τιμή</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Όσο καλύτερα τα καταλαβαίνεις, τόσο πιο εύκολα θα ξέρεις γιατί δύο σχέδια μπορεί να έχουν διαφορετικό κόστος.
            </p>
            <div className="space-y-4">
              {[
                { title: 'Το μέγεθος του σχεδίου', desc: 'Όσο μεγαλύτερο το τατουάζ, τόσο περισσότερο μελάνι, χρόνο και δουλειά χρειάζεται.' },
                { title: 'Η λεπτομέρεια και η πολυπλοκότητα', desc: 'Ένα μικρό τατουάζ μπορεί να κρύβει τεράστια λεπτομέρεια, π.χ. σκιάσεις ή πολλές λεπτές γραμμές.' },
                { title: 'Το χρώμα', desc: 'Τα έγχρωμα τατουάζ απαιτούν περισσότερο χρόνο από τα μαύρα, λόγω γεμίσματος και μετάβασης αποχρώσεων.' },
                { title: 'Το σημείο του σώματος', desc: 'Δάχτυλα, πλευρά, αυχένας ή πίσω από το αυτί έχουν πιο ευαίσθητο ή δύσκολο δέρμα.' },
                { title: 'Η εμπειρία του tattoo artist', desc: 'Πληρώνεις για την τεχνική, την ασφάλεια και το αποτέλεσμα που θα κουβαλάς για πάντα.' },
                { title: 'Ο χρόνος εργασίας', desc: 'Πολλά στούντιο έχουν ελάχιστη χρέωση για τα πολύ μικρά τατουάζ και ωριαία χρέωση για τα μεγαλύτερα.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Minimum charge */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Η έννοια <span className="text-pink-500">της ελάχιστης χρέωσης</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Σχεδόν κάθε σοβαρό στούντιο τατουάζ έχει ένα κατώτατο ποσό κάτω από το οποίο δεν κατεβαίνει, ακόμα και για το πιο μικροσκοπικό σχέδιο. Αυτό δεν είναι αυθαίρετο.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Πίσω από κάθε τατουάζ, όσο μικρό κι αν είναι, υπάρχει ολόκληρη διαδικασία: στήσιμο σταθμού εργασίας, νέες στείρες βελόνες, εξοπλισμός μιας χρήσης, γάντια, μελάνι, απολύμανση πριν και μετά. Η ελάχιστη χρέωση καλύπτει αυτό το βασικό κόστος.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Όταν ένα μικροσκοπικό τατουάζ κοστίζει περισσότερο από όσο περίμενες, δεν πληρώνεις μόνο το μελάνι στο δέρμα σου. Πληρώνεις την ασφάλεια, την καθαριότητα και τον επαγγελματισμό ενός σωστού tattoo studio.
            </p>
          </div>
        </section>

        {/* Why cheapest isn't best */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Γιατί η φθηνότερη τιμή <span className="text-pink-500">δεν είναι πάντα η καλύτερη επιλογή</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Το τατουάζ είναι κάτι μόνιμο. Η αφαίρεσή του είναι μια διαδικασία πολύ πιο δύσκολη, επώδυνη και ακριβή από το να το κάνεις σωστά εξαρχής.
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { title: 'Η υγιεινή πάνω από όλα', desc: 'Στείρες βελόνες μιας χρήσης, καθαρός εξοπλισμός και σωστή απολύμανση είναι μη διαπραγματεύσιμα.' },
                { title: 'Η ποιότητα της δουλειάς', desc: 'Ένα φθηνό, βιαστικό τατουάζ μπορεί να ξεθωριάσει, να απλωθεί ή να χάσει το σχήμα του.' },
                { title: 'Η επούλωση και το αποτέλεσμα', desc: 'Η σωστή τεχνική κάνει τη διαφορά ανάμεσα σε ένα κομμάτι που σε κάνει περήφανο και σε ένα που δεν θα ήθελες.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Session expectations */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι να περιμένεις από μια <span className="text-pink-500">συνεδρία για μικρό τατουάζ</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Η διαδικασία ξεκινάει με μια κουβέντα. Λες στον tattoo artist τι έχεις στο μυαλό σου, δείχνεις αναφορές και μαζί καταλήγετε στο τελικό σχέδιο. Ο καλλιτέχνης θα σε ρωτήσει για το μέγεθος, το σημείο και τυχόν λεπτομέρειες και θα σου δώσει την τιμή.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Πολλά μικρά κομμάτια τελειώνουν μέσα στην ίδια μέρα του ραντεβού. Πριν ξεκινήσει το χτύπημα, ο καλλιτέχνης τοποθετεί ένα στένσιλ πάνω στο δέρμα σου για να δεις ακριβώς πού και πώς θα μπει το σχέδιο, και αυτή είναι η στιγμή για τυχόν αλλαγές.
            </p>
          </div>
        </section>

        {/* Pain & first time */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Πόσο πονάει <span className="text-pink-500">ένα μικρό τατουάζ</span></h2>
                <p className="text-gray-300 leading-relaxed">
                  Ο πόνος ποικίλλει ανάλογα με το σημείο. Σημεία με περισσότερη σάρκα, όπως ο βραχίονας ή η γάμπα, είναι πιο ανεκτά. Σημεία πάνω από κόκαλο ή με λεπτό δέρμα, όπως τα πλευρά, τα δάχτυλα ή ο αστράγαλος, είναι πιο ευαίσθητα. Επειδή τα μικρά τατουάζ τελειώνουν γρήγορα, η όποια ενόχληση δεν κρατάει πολύ.
                </p>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Μικρά τατουάζ <span className="text-pink-500">και πρώτη φορά</span></h2>
                <p className="text-gray-300 leading-relaxed">
                  Ένα μικρό, διακριτικό σχέδιο σου δίνει την εμπειρία, σε βοηθάει να καταλάβεις πώς αντιδρά το δέρμα σου και σου ανοίγει την όρεξη για περισσότερα. Αν είσαι στην πρώτη σου φορά, πες το στον tattoo artist, θα σε καθησυχάσει και θα κάνει την εμπειρία πιο άνετη.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Popular designs */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Δημοφιλή σχέδια <span className="text-pink-500">για μικρό τατουάζ</span></h2>
            <div className="flex flex-wrap gap-3">
              {[
                'Μικρά λουλούδια & botanical',
                'Σύμβολα με προσωπική σημασία',
                'Lettering & ονόματα',
                'Μικρά ζώα',
                'Γεωμετρικά σχήματα',
                'Διακριτικές γραμμές',
                'Σύμβολα αγάπης',
              ].map((tag, i) => (
                <span key={i} className="border border-pink-600/20 text-gray-300 px-4 py-2 rounded-lg text-sm">{tag}</span>
              ))}
            </div>
          </div>
        </section>

        {/* Placement */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πού να βάλεις <span className="text-pink-500">το μικρό σου τατουάζ</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Καρπός', desc: 'Διακριτικό και εύκολα ορατό όταν το θέλεις.' },
                { title: 'Αυχένας / πίσω από το αυτί', desc: 'Για κάτι κομψό και κρυφό.' },
                { title: 'Αστράγαλος', desc: 'Ιδανικός για ντελικάτα σχέδια.' },
                { title: 'Δάχτυλα', desc: 'Μοντέρνα επιλογή με χαρακτήρα.' },
                { title: 'Πλευρά / κλείδα', desc: 'Για κάτι πιο προσωπικό.' },
                { title: 'Πήχης', desc: 'Εύκολος και άνετος για πρώτη φορά.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Aftercare & tips */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Η σημασία της φροντίδας <span className="text-pink-500">μετά το τατουάζ</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Ένα μεγάλο μέρος του τελικού αποτελέσματος είναι στα χέρια σου. Κράτα την περιοχή καθαρή, βάζε την ενυδατική που σου προτείνει ο καλλιτέχνης, απόφυγε τον ήλιο και την πισίνα και μην ξύνεις την περιοχή όταν αρχίσει να ξεφλουδίζει.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Στον χώρο του tattoo, το φιλοδώρημα είναι μια ωραία χειρονομία εκτίμησης προς τον καλλιτέχνη όταν είσαι ευχαριστημένος με τη δουλειά του. Δεν είναι υποχρεωτικό, αλλά εκτιμάται.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-8">Συχνές <span className="text-pink-500">Ερωτήσεις</span></h2>
            <div className="space-y-4">
              {faqSchema.mainEntity.map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-2">{item.name}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.acceptedAnswer.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Θέλεις ακριβή τιμή για <span className="text-pink-500">το δικό σου τατουάζ;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Κάθε τατουάζ είναι μοναδικό. Στείλε μήνυμα στο Instagram του 210 Tattoo Athens, δείξε την ιδέα σου και πάρε μια ξεκάθαρη τιμή για το κομμάτι που ονειρεύεσαι.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε DM για εκτίμηση
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/tattoo-cost-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πόσο κοστίζει ένα τατουάζ στην Αθήνα;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Οδηγός τιμών και παράγοντες κοστολόγησης για κάθε τύπο τατουάζ.</p>
              </Link>
              <Link to="/blog/first-tattoo-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Το Πρώτο σου Τατουάζ στην Αθήνα</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πλήρης οδηγός για πρωτάρηδες, από την επιλογή σχεδίου μέχρι την εκτέλεση.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/cover-up-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Cover Up Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
